import {v4 as uuid} from "uuid";
import {
  $KTM,
  BaseListItem,
  BaseListItemsLoader,
  KeyMetadatas,
  ListItemChange,
  OnListItemsListener,
  UserDisplayName,
  UserProfilePhoto
} from "@/shared/types";
import {Member, Members} from "@/shared/entities";
import {JsonProperty} from "@/shared/json/json-property";
import {JsonObject} from "@/shared/json/json-object";
import {md5_uuid} from "@/shared/md5";
import {getMemberAuth} from "@/shared/auth";
import {JSON_OBJECT} from "@/shared/json/helpers";
import {DirectoryUser, DirectoryUsers} from "@/shared/directory/types";
import {Rel} from "@/shared/database";
import {BaseApp} from "@/shared/BaseApp";
import {MessagesContextTrait} from "../types";
import defaultGroupPhoto from "./res/default_group.png";
import {CallChannel, CallLog, CallLogs, CallType} from "../calls/types";

export enum ParticipantType {
  MEMBER = "member",
  CUSTOMER_AGENT = "custagent",
  DIRECTORY_USER = "diruser",
  CUSTOM = "custom",
}

export type ParticipantTypeMetadata = {
  priority: number,
}

export const PARTICIPANT_TYPES_KTM = new KeyMetadatas<ParticipantTypeMetadata>([
  $KTM(ParticipantType.MEMBER, {priority: 0}),
  $KTM(ParticipantType.DIRECTORY_USER, {priority: 10}),
]);

export function DirectoryRoleParticipantKey(user: DirectoryUser, roleId: string, type: ParticipantType): ParticipantKey {
  return new ParticipantKey(user.id, type);
}

@JsonObject()
export class ParticipantKey {

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly type: ParticipantType;

  @JsonProperty()
  readonly prefixPath: string;

  constructor(
    id: string,
    type: ParticipantType,
    // prefix path for Chat[Threads/Messages].
    prefixPath?: string,
  ) {
    this.id = id;
    this.type = type;
    this.prefixPath = prefixPath;
  }

  equals(key: ParticipantKey): boolean {
    return this.id === key?.id && this.type === key?.type;
  }

  getPriority(): number {
    return PARTICIPANT_TYPES_KTM.findMetadata(this.type)?.priority || 0;
  }
}

export class ChatMeta {

  static readonly TYPE_TEXT = 0;
  static readonly TYPE_DATE = 1;

  readonly metaType: number;
  readonly id: string;
  readonly data: string;


  constructor(metaType: number, id: string, data: string) {
    this.metaType = metaType;
    this.id = id;
    this.data = data;
  }
}

export interface ChatThreadContainer {

  getParticipantKeys(): ParticipantKey[];
}

export interface ChatThreadContainerLoader {

  loadContainerRef(containerRef: string): Promise<ChatThreadContainer | null>;
}

export type ChatParticipantMetadata = {
  profilePhoto?: string,
  title?: string,
  text?: string,
}

export interface ChatThreadCustomParticipantTypeLoader {

  loadParticipantMetadata(participantId: string): Promise<ChatParticipantMetadata | null>;
}

export abstract class ChatParticipantFactory {

  static INSTANCE: ChatParticipantFactory;

  abstract loadParticipant(key: ParticipantKey): Promise<ChatParticipant | null>;

  abstract createMetadata(participant: ChatParticipant): ChatParticipantMetadata;
}

export class ChatParticipant {

  constructor(readonly key: ParticipantKey, readonly obj: DirectoryUser | Member | any) {
  }

  getMetadata(): ChatParticipantMetadata {
    return ChatParticipantFactory.INSTANCE?.createMetadata(this)
      || {
        title: UserDisplayName(this.getMember().user),
        profilePhoto: UserProfilePhoto(this.getMember().user),
      };
  }

  private getMember(): Member | null {
    switch (this.key.type) {
      case ParticipantType.DIRECTORY_USER:
        return this.asDirectoryUser()?.member;
      case ParticipantType.MEMBER:
        return this.asMember();
    }
    throw new Error("ChatParticipantMetadataFactory.INSTANCE not set.");
  }

  asDirectoryUser(): DirectoryUser | null {
    if (this.key.type !== ParticipantType.DIRECTORY_USER) {
      return null;
    }
    return this.obj as DirectoryUser;
  }

  asMember(): Member | null {
    if (this.key.type !== ParticipantType.MEMBER) {
      return null;
    }
    return this.obj as Member;
  }
}

export enum ChatMessageType {
  UNKNOWN = "unknown",
  META = "meta",
  TEXT = "text",
  CALL = "call",
}

@JsonObject()
export class ChatThread extends BaseListItem {

  readonly participantMetadata = new Map<string, ChatParticipantMetadata>();

  container?: ChatThreadContainer;

  lastMessage: ChatMessage;

  @JsonProperty()
  displayName: string;

  @JsonProperty()
  profilePhoto: string;

  @JsonProperty()
  participantKeys: ParticipantKey[];

  @JsonProperty()
  containerRef: string;

  @JsonProperty()
  lastMessageId: string;

  private _myParticipantKey: ParticipantKey;

  static createNew(participantKeys: ParticipantKey[]) {
    return new ChatThread(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), participantKeys);
  }

  constructor(id: string, creator: string, created: number, participantKeys: ParticipantKey[]) {
    super(id, creator, created);
    this.participantKeys = participantKeys;
  }

  participantsEquals(participantKeys: ParticipantKey[]): boolean {
    let currentKeys = [...this.participantKeys];
    for (const key of participantKeys) {
      const index = currentKeys.findIndex(currentKey => currentKey.id === key.id);
      if (index < 0) {
        return false;
      }
      currentKeys.splice(index, 1);
    }
    return currentKeys.length === 0;
  }

  getProfilePhoto(): string | any {
    return this.profilePhoto
      || (this.participantKeys.length === 2 && this.participantMetadata.get(this.participantKeys[1 - this.myParticipantIndex()]?.id)?.profilePhoto)
      || defaultGroupPhoto;
  }

  getDisplayName() {
    if (this.displayName) {
      return this.displayName;
    }
    const myParticipantKey = this.myParticipantKey();
    const participantKeys = this.participantKeys.filter(key => key.id !== myParticipantKey.id);
    const isSelf = this.participantKeys.length > 0 && participantKeys.length === 0;
    return this.displayName
      || (isSelf ? this.participantMetadata.get(myParticipantKey.id).title : participantKeys.map(key => this.participantMetadata.get(key.id)?.title).join(", "))
      || "[ Unknown Thread ]";
  }

  myParticipantIndex(): number {
    return this.participantKeys.findIndex(key => key.id === this._myParticipantKey?.id) || 0;
  }

  myParticipantKey(): ParticipantKey {
    return this._myParticipantKey;
  }

  _setMyParticipantKey(key: ParticipantKey): void {
    this._myParticipantKey = key;
  }

  static readonly PARTICIPANT_THREADS_REL_NAME = "ptt";

  static myParticipantThreadsRel(myParticipantKey: ParticipantKey): Rel {
    return new Rel(ChatThread.PARTICIPANT_THREADS_REL_NAME, myParticipantKey.id, null, true, true);
  }

  participantThreadsRel(): Rel[] {
    return this.participantKeys.map(key => new Rel(ChatThread.PARTICIPANT_THREADS_REL_NAME, key.id, this.id, true, true));
  }
}

export class ChatThreads extends BaseListItemsLoader<ChatThread> implements OnListItemsListener<ChatMessage> {

  private static instance: ChatThreads;

  static getInstance(type: ParticipantType = ParticipantType.CUSTOM): ChatThreads {
    if (!this.instance) {
      this.instance = new ChatThreads(BaseApp.CONTEXT.getTrait<MessagesContextTrait>().getMyParticipantKey(type));
    }
    return this.instance;
  }

  private static readonly containerLoaders: ChatThreadContainerLoader[] = [];

  static registerContainerLoader(loader: ChatThreadContainerLoader) {
    this.containerLoaders.push(loader);
  }

  private static customParticipantTypeLoader: ChatThreadCustomParticipantTypeLoader;

  static setCustomParticipantTypeLoader(loader: ChatThreadCustomParticipantTypeLoader) {
    this.customParticipantTypeLoader = loader;
  }

  private constructor(private readonly myParticipantKey: ParticipantKey) {
    super({
      shared: true,
      scopeRoot: BaseApp.CONTEXT.getAppConfig().chatScopeRoot,
      loadItemsRel: () => ChatThread.myParticipantThreadsRel(myParticipantKey),
      updateItemRels: (item: ChatThread) => item.participantThreadsRel(),
    });
  }

  protected basePath(): string {
    const prefixPath = this.myParticipantKey.prefixPath;
    return `${Boolean(prefixPath) ? prefixPath + "/" : ""}chats/threads`;
  }

  protected deserializeItem(value: any): ChatThread {
    return JSON_OBJECT.deserializeObject(value, ChatThread);
  }

  protected serializeItem(item: ChatThread): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ChatThread, item2: ChatThread): number {
    return item2.createdAt - item1.createdAt;
  }

  async onAfterItemDeserialized(item: ChatThread, val?: any): Promise<ChatThread> {
    const thread = await super.onAfterItemDeserialized(item, val);
    thread._setMyParticipantKey(this.myParticipantKey);
    const messages = ChatMessages.getInstance(this.myParticipantKey.type, thread.id);
    messages.registerObserver(this);
    for (const key of thread.participantKeys) {
      switch (key.type) {
        case ParticipantType.MEMBER: {
          const members = Members.getInstance();
          const member = (await members.getOrLoadMember(key.id)) || members.getBotMember(key.id);
          thread.participantMetadata.set(key.id, {
            title: UserDisplayName(member.user),
            text: member.user.email,
            profilePhoto: UserProfilePhoto(member.user),
          });
        }
          break;
        // The following types use DirectoryUser.id as key.id
        // @ts-ignore fall-thru
        case ParticipantType.CUSTOMER_AGENT:
        case ParticipantType.DIRECTORY_USER: {
          const dirUser = await DirectoryUsers.getInstance().getOrLoadItem(key.id);
          const member = dirUser.member;
          thread.participantMetadata.set(key.id, {
            title: UserDisplayName(member.user),
            text: member.user.email,
            profilePhoto: UserProfilePhoto(member.user),
          });
        }
          break;
        case ParticipantType.CUSTOM: {
          const metadata = await ChatThreads.customParticipantTypeLoader.loadParticipantMetadata(key.id);
          thread.participantMetadata.set(key.id, metadata);
        }
          break;
      }
    }
    if (thread.lastMessageId && thread.lastMessage?.id !== thread.lastMessageId) {
      thread.lastMessage = await messages.getOrLoadItem(thread.lastMessageId);
    }
    return thread;
  }

  onItemChanged(message: ChatMessage, change: ListItemChange) {
    switch (change) {
      case ListItemChange.ADDED:
        const chatThread = this.getListItem(message.threadId);
        // console.log("message added: " + message?.id + ", " + message?.text);
        if (chatThread && chatThread.lastMessageId !== message.id) {
          chatThread.lastMessage = message;
          chatThread.lastMessageId = message.id;
          if (message.type === ChatMessageType.CALL) {
            CallLogs.getInstance(this.myParticipantKey.type).addListItem(message.callLog);
          }

          // Only the sender updates the thread.
          if (chatThread.lastMessage?.from.id === this.myParticipantKey.id) {
            // TODO This doesn't trigger a notification to chatThread's observers, since ChatThreads loader only listens
            //  for updates to the rel (essentially if the list of threads changes, an update goes out).
            this.addListItem(chatThread);
          }
        }
        break;
    }
  }

  async addListItem(thread: ChatThread, ...rels): Promise<void> {
    if (thread.containerRef && !thread.container) {
      for (const loader of ChatThreads.containerLoaders) {
        const container = await loader.loadContainerRef(thread.containerRef);
        if (container) {
          thread.container = container;
          break;
        }
      }
    }
    return super.addListItem(thread, ...rels);
  }
}

@JsonObject()
export class ChatMessage extends BaseListItem {

  thread: ChatThread;

  @JsonProperty()
  readonly threadId: string;

  @JsonProperty()
  readonly type: ChatMessageType;

  @JsonProperty()
  from: ParticipantKey;

  meta: ChatMeta; // Not serialized, generated on the fly.

  @JsonProperty()
  text?: string; // Set only when type === ChatMessageType.TEXT

  @JsonProperty()
  callLog?: CallLog; // Set only when type === ChatMessageType.CALL

  @JsonProperty()
  deleted: boolean;

  static createNew(threadId: string, type: ChatMessageType) {
    return new ChatMessage(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), threadId, type);
  }

  constructor(id: string, creator: string, created: number, threadId: string, type: ChatMessageType) {
    super(id, creator, created);
    this.threadId = threadId;
    this.type = type;
  }

  fromMetadata(): ChatParticipantMetadata {
    return this.thread?.participantMetadata?.get(this.from.id);
  }

  isExpired(): boolean {
    return false;
  }

  isUserGenerated(default_: boolean): boolean {
    return true;
  }

  setMeta(meta: ChatMeta): ChatMessage {
    console.assert(this.type === ChatMessageType.META);
    this.meta = meta;
    return this;
  }

  setText(text: string): ChatMessage {
    console.assert(this.type === ChatMessageType.TEXT);
    this.text = text;
    return this;
  }

  setCall(callType: CallType, channel: CallChannel): ChatMessage {
    console.assert(this.type === ChatMessageType.CALL);
    this.callLog = CallLog.createNew(this.threadId, this.id);
    this.callLog.type = callType;
    this.callLog.channel = channel;
    return this;
  }
}

export class ChatMessages extends BaseListItemsLoader<ChatMessage> {

  private static readonly instances = new Map<string, ChatMessages>();

  static getInstance(type: ParticipantType, threadId: string) {
    let instance = this.instances.get(threadId);
    if (!instance) {
      instance = new ChatMessages(BaseApp.CONTEXT.getTrait<MessagesContextTrait>().getMyParticipantKey(type), threadId);
      this.instances.set(threadId, instance);
    }
    return instance;
  }

  private thread: ChatThread;

  private constructor(readonly myParticipantKey: ParticipantKey, readonly threadId: string) {
    super({
      shared: true,
      scopeRoot: BaseApp.CONTEXT.getAppConfig().chatScopeRoot,
    });
  }

  private async maybeLoadThread() {
    if (!this.thread) {
      this.thread = await ChatThreads.getInstance(this.myParticipantKey.type).getOrLoadItem(this.threadId);
    }
  }

  async loadListItems(): Promise<void> {
    await this.maybeLoadThread();
    return super.loadListItems();
  }

  async loadListItem(id: string): Promise<ChatMessage | null> {
    await this.maybeLoadThread();
    return super.loadListItem(id);
  }

  protected basePath(): string {
    const prefixPath = this.myParticipantKey.prefixPath;
    // We don't store this under 'chats/threads/<threadId>/messages' to avoid loading all messages when a thread is loaded.
    return `${Boolean(prefixPath) ? prefixPath + "/" : ""}chats/messages/${this.threadId}`;
  }

  protected deserializeItem(value: any): ChatMessage {
    return JSON_OBJECT.deserializeObject(value, ChatMessage);
  }

  protected serializeItem(item: ChatMessage): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ChatMessage, item2: ChatMessage): number {
    return item1.createdAt - item2.createdAt;
  }

  protected async onAfterItemDeserialized(item: ChatMessage, val?: any): Promise<ChatMessage> {
    console.assert(item.threadId === this.threadId);
    item.thread = this.thread;
    return super.onAfterItemDeserialized(item, val);
  }
}


export class MessageFactory {

  static createMetaMessage(threadId: string, timestamp: number): ChatMessage {
    let message = ChatMessage.createNew(threadId, ChatMessageType.META);
    message.setMeta(new ChatMeta(ChatMeta.TYPE_DATE, uuid(), "" + timestamp));
    return message;
  }

  static createTextMessage(threadId: string, from: ParticipantKey, text: string): ChatMessage {
    const message = ChatMessage.createNew(threadId, ChatMessageType.TEXT);
    message.from = from;
    message.setText(text);
    return message;
  }

  static createCallMessage(threadId: string, from: ParticipantKey, callType: CallType, channel: CallChannel): ChatMessage {
    const message = ChatMessage.createNew(threadId, ChatMessageType.CALL);
    message.from = from;
    message.setCall(callType, channel);
    return message;
  }
}